import { createEffect, createSignal } from "solid-js";
import { msg } from "@lingui/core/macro";

import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useHabitMutations } from "../hooks/use-habit-mutations";
import type { HabitWithDerived } from "../hooks/use-habits-data";

interface NoteTarget {
  habitId: string;
  habitName: string;
  dayKey: string;
  note: string;
}

/** State of day-note dialog (shared between Today and Calendar). */
export function useDayNoteDialog() {
  const [target, setTarget] = createSignal<NoteTarget | null>(null);

  return {
    target,
    open: (item: HabitWithDerived, dayKey: string, note?: string | null) => {
      setTarget({
        habitId: item.habit.id,
        habitName: item.habit.name ?? "",
        dayKey,
        note: note ?? item.todayNote ?? "",
      });
    },
    close: () => setTarget(null),
  };
}

export function DayNoteDialog(props: {
  controller: ReturnType<typeof useDayNoteDialog>;
  /**
   * URL-addressable close (#15b D4): when parent manages state in
   * `?note=`, this `onClose` clears param in addition to closing controller. If
   * omitted, dialog only closes local controller.
   */
  onClose?: () => void;
}) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const mutations = useHabitMutations();
  const [draft, setDraft] = createSignal("");

  // Note is persisted to local DB; button waits for drizzle() to exist.
  const canSave = () => Boolean(drizzle()) && !mutations.isSubmitting();

  const isOpen = () => props.controller.target() !== null;

  const close = () => {
    if (props.onClose) {
      props.onClose();
    } else {
      props.controller.close();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      close();
    }
  };

  // Re-synchronizes draft each time it opens for a different target.
  createEffect(() => {
    setDraft(props.controller.target()?.note ?? "");
  });

  const handleSave = async () => {
    const target = props.controller.target();
    if (!target) {
      return;
    }
    const value = draft().trim();
    await mutations.setCompletionNote(target.habitId, target.dayKey, value || null);
    close();
  };

  return (
    <Dialog open={isOpen()} onOpenChange={handleOpenChange}>
      <DialogHeader>
        <DialogTitle>
          {t(msg`Day note`)} — {props.controller.target()?.habitName ?? ""}
        </DialogTitle>
      </DialogHeader>
      <div class="flex flex-col gap-3 p-4 pt-2">
        <textarea
          value={draft()}
          onInput={(e) => setDraft(e.currentTarget.value)}
          rows={4}
          maxLength={500}
          placeholder={t(msg`How did it go today?`)}
          class="w-full resize-none rounded-md border border-input bg-background p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="day-note-input"
        />
        <div class="flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>
            {t(msg`Cancel`)}
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={!canSave()}
            data-testid="day-note-save"
          >
            {t(msg`Save note`)}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
